import { useTheme, jsx, Global, css } from '@emotion/react';
import React__default, { useMemo } from 'react';
import { css as css$1, sxProp } from '@indeed/ifl-css';
import { createColorProperties } from '../IndeedThemeProvider/utils.js';
import { useColorModeEXPERIMENTAL } from '../IndeedThemeProvider/hooks/useColorMode.js';
import '../IndeedThemeProvider/hooks/useColorModeAttribute.js';
import '../../hooks/useIsomorphicLayoutEffect.js';

const html = {
  // Change from `box-sizing: content-box` so that `width`
  // is not affected by `padding` or `border`.
  boxSizing: "border-box",
  "*, *::before, *::after": {
    boxSizing: "inherit"
  }
};
const body = {
  "@media print": {
    // Save printer ink.
    backgroundColor: "white"
  },
  // Add support for document.body.requestFullScreen().
  // Other elements, if background transparent, are not supported.
  "&::backdrop": {
    backgroundColor: "background"
  }
};
const baseStyles = props => css$1({
  html,
  body,
  // apply root styles to the html and body
  "html, body": {
    variant: "styles.root"
  },
  // apply element styles from theme
  variant: "styles"
})(props.theme);

/**
 * @see https://experience.indeed.design/components/ids/global-styles
 */
const GlobalStyles = props => {
  const theme = useTheme();
  const colorMode = useColorModeEXPERIMENTAL();
  const properties = useMemo(() => {
    const darkColors = theme.rawColors?.EXPERIMENTAL_dark || {};
    const lightColors = theme.rawColors?.EXPERIMENTAL_light || {};
    return createColorProperties(colorMode === "EXPERIMENTAL_dark" ? darkColors : lightColors, "colors");
  }, [theme, colorMode]);
  return jsx(React__default.Fragment, null, jsx(Global, {
    styles: /*#__PURE__*/css({
      ":root": properties
    }, process.env.NODE_ENV === "production" ? "" : ";label:GlobalStyles;", process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */")
  }), jsx(Global, {
    styles: baseStyles({
      ...props,
      theme
    })
  }), props.sx ? jsx(Global, {
    styles: sxProp({
      ...props,
      theme
    })
  }) : null);
};
GlobalStyles.displayName = "GlobalStyles";

export { GlobalStyles, body, html };
